import { isAppClient } from '@/lib/LocalStorage';
import { appRPC } from '@/lib/utils';
import { safeLocalStorage, safeSessionStorage } from '@/lib/safeStorage';

type RecoverReason =
  | 'global-error-boundary'
  | 'root-error-boundary'
  | 'window.onerror'
  | 'unhandledrejection'
  | 'chunk-load'
  | 'next-router-state'
  | 'react-310'
  | 'unknown';

type RecoverOptions = {
  /** bypass 3x modal gate and the once-per-session reload lock */
  force?: boolean;
};

function normalizeReason(input: unknown): RecoverReason {
  switch (input) {
    case 'global-error-boundary':
    case 'root-error-boundary':
    case 'window.onerror':
    case 'unhandledrejection':
    case 'chunk-load':
    case 'next-router-state':
    case 'react-310':
      return input;
    default:
      return 'unknown';
  }
}

function bumpAppRecoverCount(): number {
  try {
    const raw = safeLocalStorage.getItem('__hzc_app_recover_count__');
    const next = Math.min(999, (raw ? Number(raw) : 0) + 1);
    safeLocalStorage.setItem('__hzc_app_recover_count__', String(next));
    return Number.isFinite(next) ? next : 1;
  } catch {
    return 1;
  }
}

async function clearServiceWorkersAndCaches(): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      regs.forEach((reg) => {
        try {
          reg.unregister();
        } catch {}
      });
    }
  } catch {}

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys.map((k) => {
          try {
            return caches.delete(k);
          } catch {
            return Promise.resolve(false);
          }
        }),
      );
    }
  } catch {}
}

function reloadCurrent(force?: boolean): boolean {
  const key = '__hzc_reload_current__';
  if (!force && safeSessionStorage.getItem(key) === '1') return false;
  safeSessionStorage.setItem(key, '1');
  window.location.reload();
  return true;
}

/** User tapped “Ana sayfaya dön”. Never call this from automatic recovery. */
export function goHomeHard(): void {
  if (typeof window === 'undefined') return;
  if (isAppClient()) {
    const ok =
      appRPC('bridge', 'resetTabs', null) ||
      appRPC('route', 'navigateWithUrl', { url: '/', target: 'home' }) ||
      appRPC('route', 'routeChanged', '/');
    if (ok) return;
  }
  window.location.assign('/');
}

/**
 * Stay on the URL the user asked for. Chunk/webpack stale: drop SW/cache then
 * reload that URL once. Does not send anyone to `/`.
 */
export async function recoverCurrentPage(
  reason: RecoverReason,
  options: RecoverOptions = {},
): Promise<void> {
  if (typeof window === 'undefined') return;

  const normalizedReason = normalizeReason(reason);
  const inApp = isAppClient();

  if (inApp && !options.force) {
    const count = bumpAppRecoverCount();
    if (count >= 3 && safeSessionStorage.getItem('__hzc_app_recover_modal_shown__') !== '1') {
      safeSessionStorage.setItem('__hzc_app_recover_modal_shown__', '1');
      try {
        window.dispatchEvent(
          new CustomEvent('hzc:recover:modal', { detail: { count, reason: normalizedReason } }),
        );
      } catch {}
      return;
    }
  }

  const recoverGuardKey = `__hzc_recover_current_${normalizedReason}__`;
  if (!options.force && safeSessionStorage.getItem(recoverGuardKey) === '1') {
    return;
  }
  safeSessionStorage.setItem(recoverGuardKey, '1');

  const waitMs =
    normalizedReason === 'chunk-load' || normalizedReason === 'next-router-state' ? 400 : 1200;
  const cleanup = clearServiceWorkersAndCaches();
  await Promise.race([cleanup, new Promise((r) => setTimeout(r, waitMs))]);

  reloadCurrent(options.force);
}

/** @deprecated Use recoverCurrentPage. Kept so old call sites compile; does not go home. */
export const recoverToHome = recoverCurrentPage;
